import { useState } from "react";
import { IRestaurantAvailability } from "../models/IAvailability";
import { IWorkingHour } from "../../models/IWorkingHour";
import { reopenRestaurant } from "../services/availabilityService";
import { apiErrorMessage, crossesMidnight, friendlyDateTime, hourForDay, isoDay, parseUtc, toHHmm } from "../utils/workingHoursUtils";
import CloseNowModal from "./CloseNowModal";

interface Props {
  restaurantId: string;
  availability: IRestaurantAvailability | null;
  hours: IWorkingHour[];
  loading: boolean;
  error: string;
  onRefresh: () => void;
  /** Kapatma / açma sonrası durum ve istisnalar yenilensin */
  onChanged: () => void;
}

const REASON_TEXT: Record<string, string> = {
  OutsideWorkingHours: "Çalışma saatleri dışında",
  TemporarilyClosed: "Geçici olarak kapatıldı",
  NoWorkingHours: "Hiç çalışma saati tanımlı değil",
};

export default function AvailabilityStatusCard({ restaurantId, availability, hours, loading, error, onRefresh, onChanged }: Props) {
  const [showClose, setShowClose] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [actionError, setActionError] = useState("");

  const today = hourForDay(hours, isoDay(new Date()));
  const todayText = today
    ? today.isEnabled
      ? `${toHHmm(today.openTime)} – ${toHHmm(today.closeTime)}${crossesMidnight(today.openTime, today.closeTime) ? " (ertesi gün)" : ""}`
      : "Kapalı gün"
    : "Tanımlı değil";

  const reopen = async () => {
    setReopening(true);
    setActionError("");
    try {
      await reopenRestaurant(restaurantId);
      onChanged();
    } catch (err) {
      setActionError(apiErrorMessage(err, "Restoran açılamadı."));
    } finally {
      setReopening(false);
    }
  };

  if (!availability) {
    return (
      <div className="wh-card" style={{ padding: 18, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        {loading
          ? <span style={{ color: "#6b7280", fontSize: ".86rem" }}><span className="spinner-border spinner-border-sm me-2 text-warning" />Durum alınıyor…</span>
          : <span style={{ color: "#b91c1c", fontSize: ".86rem" }}><i className="bi bi-exclamation-circle me-1" />{error || "Durum alınamadı."}</span>}
        {!loading && <button className="wh-btn" onClick={onRefresh}><i className="bi bi-arrow-clockwise"></i>Tekrar dene</button>}
      </div>
    );
  }

  const open = availability.isOpenNow;
  const temporarilyClosed = availability.closedReason === "TemporarilyClosed";
  const specialOpen = open && availability.activeOverride && !availability.activeOverride.isClosed;
  const reopensAt = parseUtc(availability.reopensAtUtc);
  const overrideEnds = parseUtc(availability.activeOverride?.endsAtUtc);

  const accent = open ? "#059669" : temporarilyClosed ? "#dc2626" : "#6b7280";
  const bg = open ? "linear-gradient(135deg, #ecfdf5, #d1fae5)" : temporarilyClosed ? "linear-gradient(135deg, #fef2f2, #fee2e2)" : "linear-gradient(135deg, #f9fafb, #f3f4f6)";

  return (
    <>
      <div className="wh-card" style={{ overflow: "hidden" }}>
        <div style={{ background: bg, padding: "16px 18px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <span style={{
            width: 48, height: 48, borderRadius: 14, background: "#fff", color: accent, flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.4rem",
            boxShadow: `0 0 0 4px ${open ? "rgba(16,185,129,.15)" : temporarilyClosed ? "rgba(239,68,68,.12)" : "rgba(107,114,128,.12)"}`,
          }}>
            <i className={`bi ${open ? "bi-shop" : temporarilyClosed ? "bi-pause-circle-fill" : "bi-moon-stars-fill"}`}></i>
          </span>

          <div style={{ flex: "1 1 220px", minWidth: 0 }}>
            <div style={{ fontSize: ".7rem", fontWeight: 800, color: accent, textTransform: "uppercase", letterSpacing: ".06em" }}>
              Şu anki durum
            </div>
            <div style={{ fontWeight: 800, fontSize: "1.15rem", color: "#111827" }}>
              {open ? (specialOpen ? "Açık — özel açılış" : "Açık, sipariş alıyor") : `Kapalı · ${REASON_TEXT[availability.closedReason] ?? availability.closedReason}`}
            </div>
            <div style={{ fontSize: ".8rem", color: "#4b5563", marginTop: 2, display: "flex", gap: 12, flexWrap: "wrap" }}>
              {!open && (
                reopensAt
                  ? <span><i className="bi bi-alarm me-1"></i>Açılış: <b>{friendlyDateTime(reopensAt)}</b></span>
                  : temporarilyClosed
                    ? <span style={{ color: "#b91c1c", fontWeight: 700 }}><i className="bi bi-infinity me-1"></i>Süresiz — elle açılana kadar kapalı</span>
                    : null
              )}
              {specialOpen && overrideEnds && <span><i className="bi bi-alarm me-1"></i>Özel açılış bitişi: <b>{friendlyDateTime(overrideEnds)}</b></span>}
              <span><i className="bi bi-calendar-day me-1"></i>Bugünün programı: <b>{todayText}</b></span>
            </div>
            {availability.note && (
              <div style={{ fontSize: ".78rem", color: "#374151", marginTop: 6, background: "rgba(255,255,255,.7)", borderRadius: 8, padding: "5px 9px", display: "inline-block" }}>
                <i className="bi bi-chat-left-quote me-1"></i>{availability.note}
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="wh-btn" onClick={onRefresh} disabled={loading} title="Yenile" aria-label="Yenile" style={{ padding: "9px 11px" }}>
              <i className={`bi bi-arrow-clockwise${loading ? " spin" : ""}`}></i>
            </button>
            {temporarilyClosed ? (
              <button className="wh-btn wh-btn--success" onClick={reopen} disabled={reopening}>
                {reopening ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-play-fill"></i>}Şimdi Aç
              </button>
            ) : (
              <button className="wh-btn wh-btn--danger" onClick={() => setShowClose(true)}>
                <i className="bi bi-pause-fill"></i>Şimdi Kapat
              </button>
            )}
          </div>
        </div>

        {(!availability.isRestourantEnabled || actionError || availability.closedReason === "NoWorkingHours") && (
          <div style={{ padding: "10px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
            {!availability.isRestourantEnabled && (
              <div className="wh-note wh-note--warn">
                <i className="bi bi-exclamation-triangle-fill"></i>
                <span>Restoran hesabı <b>pasif</b>. Çalışma saatleri ne derse desin müşteriler sipariş veremez.</span>
              </div>
            )}
            {availability.closedReason === "NoWorkingHours" && (
              <div className="wh-note wh-note--warn">
                <i className="bi bi-exclamation-triangle-fill"></i>
                <span>Aktif çalışma saati yok. Aşağıdaki haftalık programdan en az bir gün tanımlayın.</span>
              </div>
            )}
            {actionError && <div className="wh-note wh-note--error"><i className="bi bi-exclamation-circle-fill"></i><span>{actionError}</span></div>}
          </div>
        )}
      </div>

      {showClose && <CloseNowModal restaurantId={restaurantId} onClose={() => setShowClose(false)} onDone={onChanged} />}
    </>
  );
}
